import { useIntl } from 'react-intl';

import { useJobPostingContext } from '@/contexts/job-posting-context';

/**
 * Pure check: are all selected openings unsupported by this channel?
 * Usable outside of React hooks (e.g. in loops / callbacks).
 */
export function areAllOpeningsUnsupported(
  unsupportedOpenings: string[] | undefined,
  selectedOpeningIds: string[],
): boolean {
  if (!unsupportedOpenings || selectedOpeningIds.length === 0) return false;
  return selectedOpeningIds.every(id => unsupportedOpenings.includes(id));
}

/**
 * Resolves whether every selected opening is unsupported by a channel and,
 * when it is, produces the user-facing explanation string. Both channel cards
 * (direct and Vonq) need the same boolean and the same copy, so the rule lives
 * here instead of being re-derived in each card or leaking into the layout.
 */
export function useUnsupportedOpenings(
  unsupportedOpenings: string[] | undefined,
  channelName: string,
) {
  const intl = useIntl();
  const { selectedOpeningIds } = useJobPostingContext();

  const hasUnsupportedOpenings = unsupportedOpenings
    ? selectedOpeningIds.some(openingId =>
        unsupportedOpenings.includes(openingId),
      )
    : false;

  const allOpeningsUnsupported = areAllOpeningsUnsupported(
    unsupportedOpenings,
    selectedOpeningIds,
  );

  const message = allOpeningsUnsupported
    ? intl.formatMessage(
        {
          defaultMessage: `{total, plural,
              one {The selected opening cannot be sponsored by {channelName} due to the location.}
              other {All {total} selected openings cannot be sponsored by {channelName} due to the location.}
            }`,
          description:
            'Message to show when a selected opening cannot be sponsored by a channel.',
        },
        {
          total: selectedOpeningIds.length,
          channelName,
        },
      )
    : null;

  return { hasUnsupportedOpenings, allOpeningsUnsupported, message };
}
